import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Loading } from "../loading";
import * as S from "./styles";

const ORDINALS = ["One", "Two", "Three", "Four", "Five", "Six"];
const MENU_WIDTH = 220;
// Só usada antes do menu existir de verdade no DOM (1ª passada de
// posicionamento) - depois disso a altura real é medida via menuRef.
const MENU_ESTIMATED_HEIGHT = 200;
const VIEWPORT_GAP = 8;
const TRIGGER_GAP = 6;

const DotsIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="3.5" r="1.5" fill="#21262F" />
        <circle cx="9" cy="9" r="1.5" fill="#21262F" />
        <circle cx="9" cy="14.5" r="1.5" fill="#21262F" />
    </svg>
);

// Menu de "mais opções" (botão de três pontinhos) genérico: cada opção vem
// via props numeradas - OneOptionIcon/OneOptionText/OneOptionAction,
// TwoOptionIcon/TwoOptionText/TwoOptionAction, e assim por diante até Six.
// Uma opção só aparece se tiver OptionText preenchido; OptionDivider
// desenha uma linha separadora acima dela, OptionDanger deixa ela (ícone +
// texto) na cor de perigo (ex.: "Inativar ART/TRT") e OptionSuccess na cor
// verde AgroReceita (ex.: "Ativar ART/TRT").
//
// O menu é renderizado via portal em document.body, com posição calculada
// a partir do botão de gatilho (mesmo esquema usado pelos painéis
// flutuantes de TableWithLabelBlack/indexV2.js). Isso é necessário porque,
// dentro de uma linha de tabela, um dropdown com position:absolute comum
// fica cortado pelo scroll horizontal da tabela (ou distorce a altura da
// linha) e nunca aparece de verdade.
const MoreOptions = (props) => {
    const { loading = false } = props;
    const wrapperRef = useRef(null);
    const menuRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState(null);

    // Se um loading começar enquanto o menu tiver aberto, fecha - o gatilho
    // vira o spinner e não faz sentido deixar o menu flutuando aberto.
    useEffect(() => {
        if (loading) setIsOpen(false);
    }, [loading]);

    const updatePosition = useCallback(() => {
        const trigger = wrapperRef.current;
        if (!trigger || typeof window === 'undefined') return;

        const rect = trigger.getBoundingClientRect();
        // 1ª passada: o menu ainda não existe no DOM, usa a estimativa. A
        // partir da 2ª passada (depois que ele monta) mede a altura real -
        // sem isso, uma estimativa alta demais faz o menu "pular" muito mais
        // pra cima do que precisa quando abre perto do fim da tela.
        const menuHeight = menuRef.current ? menuRef.current.getBoundingClientRect().height : MENU_ESTIMATED_HEIGHT;

        // Por padrão abre encostando a borda direita do menu na borda
        // direita do botão (menu "cresce" pra esquerda). Se não sobrar
        // espaço à esquerda pra isso (ex.: gatilho perto da borda esquerda
        // da tela, como na coluna AÇÕES), inverte e alinha pela esquerda do
        // botão (menu "cresce" pra direita) - evita ficar cortado/atrás de
        // elementos fixos como a navbar lateral.
        let left = rect.right - MENU_WIDTH;
        if (left < VIEWPORT_GAP) {
            left = rect.left;
        }
        left = Math.max(VIEWPORT_GAP, Math.min(left, window.innerWidth - MENU_WIDTH - VIEWPORT_GAP));

        let top = rect.bottom + TRIGGER_GAP;
        if (top + menuHeight > window.innerHeight - VIEWPORT_GAP) {
            top = Math.max(VIEWPORT_GAP, rect.top - menuHeight - TRIGGER_GAP);
        }

        // Evita um novo objeto (e um novo re-render) quando o valor não
        // mudou - é o que faz a 2ª passada (medição real) parar de se
        // re-disparar depois de convergir.
        setPosition((prev) => (prev && prev.top === top && prev.left === left) ? prev : { top, left });
    }, []);

    useLayoutEffect(() => {
        if (isOpen) {
            updatePosition();
        } else {
            setPosition(null);
        }
    }, [isOpen, updatePosition]);

    // 2ª passada: agora que `position` está setado, o menu foi montado no
    // DOM (ainda que com a posição estimada) - remede com a altura real.
    useLayoutEffect(() => {
        if (isOpen && position) updatePosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, position, updatePosition]);

    useEffect(() => {
        if (!isOpen) return undefined;

        function handleClickOutside(event) {
            const clickedTrigger = wrapperRef.current && wrapperRef.current.contains(event.target);
            const clickedMenu = menuRef.current && menuRef.current.contains(event.target);
            if (!clickedTrigger && !clickedMenu) setIsOpen(false);
        }

        // Fecha em vez de tentar reposicionar - evita o menu "flutuando"
        // desalinhado enquanto a tabela rola horizontalmente.
        function handleScrollOrResize() {
            setIsOpen(false);
        }

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("resize", handleScrollOrResize);
        // capture:true pra pegar o scroll de containers internos (a tabela
        // rola horizontalmente por dentro), que não borbulha pro window.
        window.addEventListener("scroll", handleScrollOrResize, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("resize", handleScrollOrResize);
            window.removeEventListener("scroll", handleScrollOrResize, true);
        };
    }, [isOpen]);

    const options = ORDINALS
        .map((ordinal) => ({
            icon: props[`${ordinal}OptionIcon`],
            text: props[`${ordinal}OptionText`],
            action: props[`${ordinal}OptionAction`],
            divider: props[`${ordinal}OptionDivider`],
            danger: props[`${ordinal}OptionDanger`],
            success: props[`${ordinal}OptionSuccess`],
        }))
        .filter((option) => option.text);

    const handleOptionClick = (action) => {
        setIsOpen(false);
        if (action) action();
    };

    return (
        <S.Wrapper ref={wrapperRef}>
            <S.TriggerButton
                type="button"
                aria-label="Mais opções"
                disabled={loading}
                onClick={(e) => {
                    // Evita disparar o onClick de um pai clicável (ex.: um
                    // card inteiro que navega ao ser clicado).
                    e.stopPropagation();
                    if (!loading) setIsOpen((prev) => !prev);
                }}
            >
                {loading ? <Loading size="16px" style={{ margin: 0 }} /> : <DotsIcon />}
            </S.TriggerButton>

            {isOpen && position && typeof document !== 'undefined' && createPortal(
                <S.Menu ref={menuRef} style={{ position: 'fixed', top: position.top, left: position.left, width: MENU_WIDTH }}>
                    {options.map((option, index) => (
                        <React.Fragment key={index}>
                            {option.divider && <S.Divider />}
                            <S.MenuItem
                                type="button"
                                danger={option.danger}
                                success={option.success}
                                onClick={() => handleOptionClick(option.action)}
                            >
                                {option.icon && (
                                    <S.MenuItemIcon danger={option.danger} success={option.success}>
                                        {option.icon}
                                    </S.MenuItemIcon>
                                )}
                                <S.MenuItemText danger={option.danger} success={option.success}>{option.text}</S.MenuItemText>
                            </S.MenuItem>
                        </React.Fragment>
                    ))}
                </S.Menu>,
                document.body
            )}
        </S.Wrapper>
    );
};

export default MoreOptions;
